import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { statuteEstimateQueryOptions } from "@/lib/data/react-query/matters/statute-estimate";
import type { Matter } from "@/lib/types";
import {
  getlabel,
  getMatterContactConnections,
} from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useState } from "react";

export function MatterStatuteEstimate({
  matter,
  defendantId,
  onApply,
}: {
  matter: Matter;
  defendantId: string;
  onApply: (date: string) => void;
}) {
  const clients = getMatterContactConnections(matter).filter(
    (connection) => getlabel(connection) === "Client",
  );
  const [clientId, setClientId] = useState(
    clients.length === 1 ? clients[0].contactId : "",
  );
  const estimate = useQuery(
    statuteEstimateQueryOptions(
      { matterId: matter.id, clientId, defendantId },
      matter.dateModified,
    ),
  );
  const canEstimate = Boolean(clientId && defendantId);
  const publicErrorMessage = isAxiosError<{ meta?: { userMsg?: string } }>(
    estimate.error,
  )
    ? estimate.error.response?.data.meta?.userMsg
    : undefined;
  return (
    <div className="col-span-2 space-y-3 rounded-lg border p-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={clientId} onValueChange={setClientId}>
          <SelectTrigger
            aria-label="Client for statute estimate"
            className="min-w-48 flex-1"
          >
            <SelectValue placeholder="Select client for estimate" />
          </SelectTrigger>
          <SelectContent>
            {clients.map((connection) => (
              <SelectItem key={connection.id} value={connection.contactId}>
                {connection.contact.title ||
                  [
                    connection.contact.individualFirstName,
                    connection.contact.individualLastName,
                  ]
                    .filter(Boolean)
                    .join(" ") ||
                  "Client"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          disabled={!canEstimate || estimate.isFetching}
          onClick={() => {
            void estimate.refetch();
          }}
        >
          {estimate.isFetching ? "Estimating…" : "Estimate SOL"}
        </Button>
      </div>
      {!defendantId && (
        <p className="text-muted-foreground text-sm">
          Select a defendant before estimating.
        </p>
      )}
      {estimate.isError && (
        <p role="alert" className="text-destructive text-sm">
          {publicErrorMessage ||
            "An estimate is unavailable. Check the matter’s incident type, incident details and active client and defendant."}
        </p>
      )}
      {estimate.data && (
        <div className="space-y-3">
          {!estimate.data.data.coverageComplete && (
            <p role="status" className="text-sm text-amber-700">
              Some claims have no applicable rule. These estimates do not cover
              every selected claim.
            </p>
          )}
          {estimate.data.data.results.map((result) => (
            <div
              key={result.claimTypeId ?? "general"}
              className="space-y-1 border-t pt-3"
            >
              <p className="text-sm font-medium">
                {result.claimTypeLabel ?? "Incident category"}
              </p>
              {result.status === "supported" && result.suggestedDate ? (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm">{result.suggestedDate}</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (result.suggestedDate) onApply(result.suggestedDate);
                    }}
                  >
                    Use this date
                  </Button>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">
                  {result.reason ?? "No applicable rule is available."}
                </p>
              )}
              {result.sourceCitation && (
                <p className="text-muted-foreground text-xs">
                  {result.sourceCitation}
                </p>
              )}
              {result.effectiveFrom && (
                <p className="text-muted-foreground text-xs">
                  Rule effective {result.effectiveFrom}
                </p>
              )}
            </div>
          ))}
          <p className="text-muted-foreground text-xs">
            Using an estimate updates the form. Save to change this deadline.
          </p>
        </div>
      )}
    </div>
  );
}
